import type {
  AddLibraryBlocksToSubmissionPayload,
  AddLibraryBlockToSubmissionPayloadInput,
  AddLibraryBlockToTemplatePayloadInput,
  AddOrRemoveDataSectionsPayload,
  ApplyEndorsementPayload,
  BulkDeleteContractBlocksPayload,
  BulkDeleteSummaryBlocksPayload,
  ChangeSubmissionBaseTemplatePayload,
  CreateContractSectionPayloadInput,
  DeleteContractBlockPayload,
  DeleteContractSectionPayload,
  PreDraftToDraftPayload,
  RecreatePdfWithAttachmentPayload,
  RemoveResourceAttachmentPayload,
  RenameResourceAttachmentPayload,
  ReopenCommentsThreadPayload,
  ReorderContractBlockPayload,
  ReorderContractSectionPayload,
  ResolveCommentsThreadPayload,
  RestoreEndorsementSummaryTemplatePayload,
  SyncSubmissionDataPayload,
  UpdateContractSectionPayload,
  UpdateResourceAttachmentStatusPayload,
} from '@domain/schemas/payload.schema';
import { getCurrentDocumentApiUrl } from '@helpers/getCurrentDocumentApiUrl';
import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { ContractPatchResponse } from '@responses/common';
import axios from 'axios';

import { queryClient } from '../utils/queryClient';

import type {
  CreateResourceBlockApiPayload,
  UpdateResourceBlockApiPayload,
  UpdateResourceDataApiPayload,
} from './payloads';

/**
 * Every body the SPA sends to `PATCH /<resource>/:id`, discriminated by `operation`. Typed from the route schemas
 * in `@domain/schemas/payload.schema`, except the SPA-loosened block payloads kept in `@types/payloads`.
 */
type PatchEntityData =
  | AddLibraryBlockToSubmissionPayloadInput
  | AddLibraryBlockToTemplatePayloadInput
  | AddLibraryBlocksToSubmissionPayload
  | AddOrRemoveDataSectionsPayload
  | ApplyEndorsementPayload
  | BulkDeleteContractBlocksPayload
  | BulkDeleteSummaryBlocksPayload
  | ChangeSubmissionBaseTemplatePayload
  | CreateResourceBlockApiPayload
  | CreateContractSectionPayloadInput
  | DeleteContractBlockPayload
  | DeleteContractSectionPayload
  | PreDraftToDraftPayload
  | RecreatePdfWithAttachmentPayload
  | RemoveResourceAttachmentPayload
  | RenameResourceAttachmentPayload
  | ReopenCommentsThreadPayload
  | ReorderContractBlockPayload
  | ReorderContractSectionPayload
  | ResolveCommentsThreadPayload
  | RestoreEndorsementSummaryTemplatePayload
  | SyncSubmissionDataPayload
  | UpdateContractSectionPayload
  | UpdateResourceAttachmentStatusPayload
  | UpdateResourceBlockApiPayload
  | UpdateResourceDataApiPayload;

// The body keeps its union type so object literals still get excess-property checks; `Op` only picks the response.
export const patchEntity = async <Op extends PatchEntityData['operation']>(
  data: PatchEntityData & { operation: Op },
  entityId?: string,
): Promise<ContractPatchResponse<Op>> => {
  const url = getCurrentDocumentApiUrl(location.pathname);

  const networkPayload = await getRequestAuthPayload({
    method: 'patch',
    url: entityId ? `${url}/${entityId}` : url,
    data,
  });

  const response = await axios(networkPayload);
  await queryClient.invalidateQueries({ queryKey: ['submissions'] });
  await queryClient.invalidateQueries({ queryKey: ['search'] });
  return response.data;
};
